"use client";

import { toast } from "sonner";
import i18next from "@/lib/i18n";

const t = (k: string): string => String(i18next.t(k));

type PinturaModule = typeof import("@pqina/pintura");
type PinturaVideoModule = typeof import("@pqina/pintura-video");

export type PinturaImageModalResult = {
  blob: Blob;
  width?: number;
  height?: number;
  /** Convenience `"{width}x{height}"` when dimensions are known */
  size?: string;
};

export type PinturaVideoModalResult = {
  file: File;
  width?: number;
  height?: number;
  durationSeconds?: number;
  /** Convenience `"{width}x{height}"` when dimensions are known */
  size?: string;
  /** Convenience `"{width}x{height}"` for Video node aspectRatio field */
  aspectRatio?: string;
};

export type OpenPinturaImageModalOptions = {
  src: string | File;
  cropAspectRatio?: number | string;
  enableTheme?: boolean;
  hideDecorateTab?: boolean;
};

export type OpenPinturaVideoModalOptions = {
  src: string | File;
  fps?: number;
  mimeType?: string;
  enableTheme?: boolean;
  hideDecorateTab?: boolean;
};

const parseCropAspectRatio = (value?: number | string): number | undefined => {
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }
  if (!value) return undefined;

  const s = String(value).trim();
  if (!s) return undefined;

  // common "free" tokens
  if (/^(free|auto|original)$/i.test(s)) return undefined;

  // "16/9", "16:9", "16x9"
  const m = s.match(/^(\d+(?:\.\d+)?)\s*[:/x]\s*(\d+(?:\.\d+)?)$/i);
  if (m) {
    const w = Number(m[1]);
    const h = Number(m[2]);
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) return w / h;
    return undefined;
  }

  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

const getIsZhUi = () => {
  const docLang =
    (typeof document !== "undefined" && document.documentElement?.lang) || "";
  const navLang =
    (typeof navigator !== "undefined" && navigator.language) || "";
  return /^(zh)(-|$)/i.test(docLang) || /^(zh)(-|$)/i.test(navLang);
};

const getDefaultCropPresetOptions = (isZhUi: boolean) => [
  [undefined, isZhUi ? "自由" : "Free"],
  [21 / 9, "21:9"],
  [16 / 9, "16:9"],
  [16 / 10, "16:10"],
  [5 / 4, "5:4"],
  [4 / 3, "4:3"],
  [3 / 2, "3:2"],
  [1, "1:1"],
  [2 / 3, "2:3"],
  [3 / 4, "3:4"],
  [4 / 5, "4:5"],
  [10 / 16, "10:16"],
  [9 / 16, "9:16"],
];

const getDefaultStickers = (isZhUi: boolean) => {
  return [
    [
      isZhUi ? "贴纸" : "Stickers",
      ["⭐️", "🔥", "✅", "❌", "😊", "👍", "👎", "➡️", "⬅️", "⬆️", "⬇️"],
    ],
  ];
};

const createThemeEl = () => {
  const themeEl = document.createElement("style");
  themeEl.dataset.pinturaTheme = "true";
  const baseBg = "255, 255, 255";
  const baseFg = "0, 0, 0";
  const darkBg = "17, 17, 17";
  const darkFg = "255, 255, 255";
  themeEl.textContent = `
    .pintura-editor{--color-transition-duration:120ms;}
    :root .pintura-editor{--color-background:${baseBg};--color-foreground:${baseFg};}
    :root.dark .pintura-editor{--color-background:${darkBg};--color-foreground:${darkFg};}
    @media (prefers-color-scheme: dark){
      :root:not(.light) .pintura-editor{--color-background:${darkBg};--color-foreground:${darkFg};}
    }
  `;
  return themeEl;
};

const injectStyles = (opts: { video?: boolean; enableTheme?: boolean }) => {
  const cleanupFns: Array<() => void> = [];
  const addLink = (href: string) => {
    try {
      const el = document.createElement("link");
      el.rel = "stylesheet";
      el.href = href;
      el.dataset.pinturaTemp = "true";
      document.head.appendChild(el);
      cleanupFns.push(() => {
        try {
          document.head.removeChild(el);
        } catch {}
      });
    } catch {}
  };

  addLink("https://unpkg.com/@pqina/pintura/pintura.css");
  if (opts.video) addLink("https://unpkg.com/@pqina/pintura-video/pinturavideo.css");

  if (opts.enableTheme) {
    try {
      const themeEl = createThemeEl();
      document.head.appendChild(themeEl);
      cleanupFns.push(() => {
        try {
          document.head.removeChild(themeEl);
        } catch {}
      });
    } catch {}
  }

  return () => {
    for (const fn of cleanupFns.reverse()) fn();
  };
};

const hideDecorateTab =
  (enabled: boolean) =>
  (tabs: any[]) => {
    if (!enabled) return tabs;
    const next = (tabs || []).filter((t: any) => String(t?.id) !== "decorate");
    // if removed the selected tab, pick the first remaining
    if (next.length > 0 && !next.some((t: any) => t?.selected)) {
      return next.map((t: any, idx: number) =>
        idx === 0 ? { ...t, selected: true } : { ...t, selected: false }
      );
    }
    return next;
  };

const didChange = (editor: any) => {
  try {
    const idx = editor?.history?.index ?? 0;
    const len = editor?.history?.length ?? 0;
    return idx > 0 || len > 1;
  } catch {
    return true;
  }
};

const getProcessError = (err: any) => {
  try {
    return (err && (err.error || err.detail?.error || err.cause)) ?? err;
  } catch {
    return err;
  }
};

const getErrorMessage = (err: any) => {
  const e = getProcessError(err);
  try {
    if (!e) return "";
    if (typeof e === "string") return e;
    if (e instanceof Error) return e.message;
    if (typeof e?.message === "string") return e.message;
    return JSON.stringify(e);
  } catch {
    return String(err);
  }
};

const resolveRemoteSrcToFile = async (
  src: string | File,
  fallbackType?: string,
  filename = "media"
): Promise<File | string> => {
  // Only try to fetch remote URLs to avoid tainted canvas issues during encode.
  if (typeof src !== "string" || !/^https?:\/\//i.test(src)) return src;
  try {
    const resp = await fetch(src, { mode: "cors", cache: "no-store" });
    if (!resp.ok) return src;
    const blob = await resp.blob();
    const type = blob.type || fallbackType || "";
    return new File([blob], `${filename}-${Date.now()}`, type ? { type } : undefined);
  } catch {
    return src;
  }
};

const isMediaRecorderTypeSupported = (mimeType: string) => {
  try {
    const MR: any = (globalThis as any)?.MediaRecorder;
    if (!MR || typeof MR.isTypeSupported !== "function") return false;
    return Boolean(MR.isTypeSupported(mimeType));
  } catch {
    return false;
  }
};

const pickMediaStreamMimeType = (preferred?: string): string => {
  const candidates = [
    preferred,
    // mp4 first (Safari/Apple ecosystem)
    "video/mp4",
    'video/mp4;codecs="avc1.42E01E,mp4a.40.2"',
    // webm variants (Chromium/Firefox)
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ].filter(Boolean) as string[];

  for (const t of candidates) {
    if (isMediaRecorderTypeSupported(t)) return t;
  }

  // Fallback: keep previous default; encoder may still decide
  return preferred || "video/webm";
};

const isFirefox = () => {
  try {
    if (typeof navigator === "undefined") return false;
    return /firefox/i.test(navigator.userAgent);
  } catch {
    return false;
  }
};

const alignTo = (n: number, step: number) => Math.ceil(n / step) * step;

const getVideoDimensionsFromSrc = async (
  src: File | string
): Promise<{ width: number; height: number } | null> => {
  try {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;

    const url = typeof src === "string" ? src : URL.createObjectURL(src);
    const cleanup = () => {
      try {
        if (typeof src !== "string") URL.revokeObjectURL(url);
      } catch {}
      try {
        video.src = "";
      } catch {}
    };

    return await new Promise((resolve) => {
      const done = (res: { width: number; height: number } | null) => {
        cleanup();
        resolve(res);
      };
      video.onloadedmetadata = () => {
        const width = Number(video.videoWidth);
        const height = Number(video.videoHeight);
        if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
          done({ width, height });
        } else {
          done(null);
        }
      };
      video.onerror = () => done(null);
      video.src = url;
    });
  } catch {
    return null;
  }
};

const supportsVideoEncoderConfig = async (config: any): Promise<boolean> => {
  try {
    if (!config || !config.codec || !config.width || !config.height) return true;
    const VE: any = (globalThis as any)?.VideoEncoder;
    if (!VE || typeof VE.isConfigSupported !== "function") return true;
    const res = await VE.isConfigSupported(config);
    return Boolean(res?.supported);
  } catch {
    return true;
  }
};

const calcDownscaleToCodedAreaLimit = (
  width: number,
  height: number,
  codedAreaLimit: number
) => {
  const codedW = alignTo(width, 16);
  const codedH = alignTo(height, 16);
  const area = codedW * codedH;
  if (!Number.isFinite(area) || area <= 0 || area <= codedAreaLimit) return null;
  const scale = Math.sqrt(codedAreaLimit / area);
  const w2 = Math.max(16, Math.floor(width * scale));
  const h2 = Math.max(16, Math.floor(height * scale));
  return { width: w2, height: h2 };
};

const getImageDimensionsFromBlob = async (
  blob: Blob
): Promise<{ width: number; height: number } | null> => {
  // Prefer createImageBitmap (fast, no DOM), fallback to HTMLImageElement
  try {
    const hasCreateImageBitmap = typeof createImageBitmap === "function";
    if (hasCreateImageBitmap) {
      const bmp = await createImageBitmap(blob);
      const width = Number((bmp as any)?.width);
      const height = Number((bmp as any)?.height);
      try {
        bmp.close?.();
      } catch {}
      if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
        return { width, height };
      }
    }
  } catch {}

  try {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.decoding = "async";
    const cleanup = () => {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    };
    try {
      img.src = url;
      // decode is nicer but not universally reliable; fall back to onload
      if (typeof (img as any).decode === "function") {
        await (img as any).decode();
      } else {
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error("Image load failed"));
        });
      }
      const width = Number(img.naturalWidth || img.width);
      const height = Number(img.naturalHeight || img.height);
      cleanup();
      if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
        return { width, height };
      }
      return null;
    } catch {
      cleanup();
      return null;
    }
  } catch {
    return null;
  }
};

const getVideoMetadataFromFile = async (
  file: File
): Promise<{ width: number; height: number; durationSeconds: number } | null> => {
  try {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    const cleanup = () => {
      try {
        URL.revokeObjectURL(url);
      } catch {}
      try {
        video.src = "";
      } catch {}
    };
    return await new Promise((resolve) => {
      const done = (res: { width: number; height: number; durationSeconds: number } | null) => {
        cleanup();
        resolve(res);
      };
      video.onloadedmetadata = () => {
        const width = Number(video.videoWidth);
        const height = Number(video.videoHeight);
        const dur = Number(video.duration);
        const durationSeconds = Number.isFinite(dur) && dur > 0 ? Math.round(dur * 100) / 100 : 0;
        if (
          Number.isFinite(width) &&
          Number.isFinite(height) &&
          width > 0 &&
          height > 0
        ) {
          done({ width, height, durationSeconds });
        } else {
          done(null);
        }
      };
      video.onerror = () => done(null);
      video.src = url;
    });
  } catch {
    return null;
  }
};

export async function openPinturaImageModal(
  options: OpenPinturaImageModalOptions
): Promise<PinturaImageModalResult | null> {
  try {
    const isZhUi = getIsZhUi();
    const zhLocale = isZhUi
      ? await import("@pqina/pintura/locale/zh_CN")
          .then((m) => (m as any).default)
          .catch(() => undefined)
      : undefined;
    const locale = zhLocale ? { ...zhLocale, redactLabel: "马赛克" } : undefined;

    const cleanup = injectStyles({ enableTheme: options.enableTheme ?? true });

    const pintura: PinturaModule = await import("@pqina/pintura");
    const ratio = parseCropAspectRatio(options.cropAspectRatio);
    const resolvedSrc = await resolveRemoteSrcToFile(options.src, undefined, "image");

    return await new Promise<PinturaImageModalResult | null>((resolve) => {
      let finished = false;
      const finalize = (result: PinturaImageModalResult | null) => {
        if (finished) return;
        finished = true;
        resolve(result);
        cleanup();
      };

      const editor = (pintura as any).openDefaultEditor({
        ...(locale ? { src: resolvedSrc, locale } : { src: resolvedSrc }),
        cropEnableSelectPreset: true,
        cropSelectPresetOptions: getDefaultCropPresetOptions(isZhUi),
        imageCropAspectRatio: ratio,
        stickers: getDefaultStickers(isZhUi),
        stickerEnableSelectImagePreset: true,
        willRenderUtilTabs: hideDecorateTab(options.hideDecorateTab ?? true),
      });

      editor.on("processerror", (err: any) => {
        console.error("[PinturaImage] processerror meta", err);
        console.error("[PinturaImage] processerror.error", getProcessError(err));
        try {
          toast.error(getErrorMessage(err) || "Error processing image");
        } catch {}
        finalize(null);
        try {
          editor.close();
        } catch {}
      });

      editor.on("process", (e: any) => {
        if (!didChange(editor)) {
          finalize(null);
          try {
            editor.close();
          } catch {}
          return;
        }
        const dest = (e?.dest as Blob | null) ?? null;
        if (!dest) {
          finalize(null);
          try {
            editor.close();
          } catch {}
          return;
        }
        void (async () => {
          const dims = await getImageDimensionsFromBlob(dest);
          const result: PinturaImageModalResult = dims
            ? { blob: dest, width: dims.width, height: dims.height, size: `${dims.width}x${dims.height}` }
            : { blob: dest };
          finalize(result);
          try {
            editor.close();
          } catch {}
        })();
      });

      editor.on("close", () => finalize(null));
      editor.on("loaderror", () => {
        try {
          toast.error(t("Failed to load image in editor"));
        } catch {}
        finalize(null);
      });
    });
  } catch (e) {
    console.error(e);
    try {
      toast.error(t("Failed to open editor"));
    } catch {}
    return null;
  }
}

export async function openPinturaVideoModal(
  options: OpenPinturaVideoModalOptions
): Promise<PinturaVideoModalResult | null> {
  try {
    const isZhUi = getIsZhUi();
    const zhLocale = isZhUi
      ? await import("@pqina/pintura/locale/zh_CN")
          .then((m) => (m as any).default)
          .catch(() => undefined)
      : undefined;

    const pintura: PinturaModule = await import("@pqina/pintura");
    const pinturaVideo: PinturaVideoModule = await import("@pqina/pintura-video");

    const locale = {
      ...(zhLocale || {}),
      ...(pinturaVideo as any).plugin_trim_locale_en_gb,
    };

    const cleanup = injectStyles({
      video: true,
      enableTheme: options.enableTheme ?? true,
    });

    // 注册视频 Trim 插件
    (pintura as any).setPlugins((pinturaVideo as any).plugin_trim);

    // Resolve remote URL into a File when possible (helps avoid tainted canvas + encode issues)
    const resolvedSrc = await resolveRemoteSrcToFile(
      options.src,
      options.mimeType,
      "video"
    );

    // Prefer MuxerEncoder (WebCodecs + mp4-muxer) when compatible; otherwise fallback to MediaStreamEncoder.
    let writer: any | undefined;
    let chosenMime: string | undefined = options.mimeType;
    let encoderKind: "muxer" | "mediastream" = "mediastream";
    try {
      const supportsVideoEncoder =
        typeof window !== "undefined" && "VideoEncoder" in (window as any);
      if (supportsVideoEncoder) {
        const Mp4Muxer = await import("mp4-muxer").catch(() => null as any);
        if (Mp4Muxer) {
          const AVC_LEVEL_4_CODED_AREA_LIMIT = 2097152; // from WebCodecs error message (≈1080p)
          const srcDims = await getVideoDimensionsFromSrc(resolvedSrc as any);
          const codedW = srcDims ? alignTo(srcDims.width, 16) : undefined;
          const codedH = srcDims ? alignTo(srcDims.height, 16) : undefined;
          const codedArea =
            typeof codedW === "number" && typeof codedH === "number"
              ? codedW * codedH
              : undefined;

          // Default mp4 muxer uses avc1.640028 (H.264 High Profile, level 4.0).
          // For higher resolutions, bump to level 5.1 (0x33) if supported.
          const needsHigherLevel =
            typeof codedArea === "number" ? codedArea > AVC_LEVEL_4_CODED_AREA_LIMIT : false;
          let videoCodec = needsHigherLevel ? "avc1.640033" : "avc1.640028";
          let targetSizeOverride:
            | { width?: number; height?: number; fit?: "contain"; upscale?: boolean }
            | undefined;

          const canUseCodec = await supportsVideoEncoderConfig({
            codec: videoCodec,
            width: codedW,
            height: codedH,
            framerate: options.fps ?? 30,
            bitrate: 2_000_000,
          });

          if (!canUseCodec && needsHigherLevel && srcDims) {
            // If the device can't do level 5.1, downscale to fit level 4.0 area limit.
            const ds = calcDownscaleToCodedAreaLimit(
              srcDims.width,
              srcDims.height,
              AVC_LEVEL_4_CODED_AREA_LIMIT
            );
            if (ds) {
              targetSizeOverride = {
                width: ds.width,
                height: ds.height,
                fit: "contain",
                upscale: false,
              };
              videoCodec = "avc1.640028";
            }
          }

          // If we still can't use the codec (and no downscale override), skip muxer.
          if (!canUseCodec && !targetSizeOverride) {
            throw new Error("Muxer encoder not supported for this video configuration");
          }

          const encoderAudioCodec = isFirefox() ? "opus" : "mp4a.40.2";
          const muxerAudioCodec = isFirefox() ? "opus" : "aac";

          const muxerEncoder = (pinturaVideo as any).createMuxerEncoder({
            muxer: Mp4Muxer,
            mimeType: "video/mp4",
            imageStateToCanvas: (pintura as any).imageStateToCanvas,
            framesPerSecond: options.fps ?? 30,
            codecs: {
              muxer: { video: "avc", audio: muxerAudioCodec },
              encoder: { video: videoCodec, audio: encoderAudioCodec },
            },
            log: process.env.NODE_ENV !== "production",
          });
          writer = (pinturaVideo as any).createDefaultVideoWriter({
            encoder: muxerEncoder,
            ...(targetSizeOverride ? { targetSize: targetSizeOverride } : {}),
          });
          chosenMime = "video/mp4";
          encoderKind = "muxer";
        }
      }
    } catch {}

    if (!writer) {
      const chosen = pickMediaStreamMimeType(options.mimeType);
      const mediaStreamEncoder = (pinturaVideo as any).createMediaStreamEncoder({
        imageStateToCanvas: (pintura as any).imageStateToCanvas,
        framesPerSecond: options.fps ?? 30,
        mimeType: chosen,
        log: process.env.NODE_ENV !== "production",
      });
      writer = (pinturaVideo as any).createDefaultVideoWriter({
        encoder: mediaStreamEncoder,
      });
      if (!chosenMime) chosenMime = chosen;
    }

    return await new Promise<PinturaVideoModalResult | null>((resolve) => {
      let finished = false;
      const finalize = (result: PinturaVideoModalResult | null) => {
        if (finished) return;
        finished = true;
        resolve(result);
        cleanup();
      };

      const editor = (pintura as any).openDefaultEditor({
        src: resolvedSrc,
        imageWriter: writer,
        locale,
        enableButtonExport: true,
        cropEnableSelectPreset: true,
        cropSelectPresetOptions: getDefaultCropPresetOptions(isZhUi),
        stickers: getDefaultStickers(isZhUi),
        stickerEnableSelectImagePreset: true,
        willRenderUtilTabs: hideDecorateTab(options.hideDecorateTab ?? true),
      });

      editor.on("processerror", (err: any) => {
        // This is the underlying reason behind the generic "Error processing media" status
        console.error(`[PinturaVideo] processerror (${encoderKind}) meta`, err);
        console.error(`[PinturaVideo] processerror (${encoderKind}) error`, getProcessError(err));
        try {
          toast.error(getErrorMessage(err) || "Error processing media");
        } catch {}
        finalize(null);
        try {
          editor.close();
        } catch {}
      });

      editor.on("process", (e: any) => {
        if (!didChange(editor)) {
          finalize(null);
          editor.close();
          return;
        }

        const dest = e?.dest as File | Blob;
        let file: File | null = null;
        try {
          if (dest instanceof File) {
            file = dest;
          } else if (dest) {
            const effectiveMime = (dest as any)?.type || chosenMime || "video/mp4";
            const ext = effectiveMime.includes("mp4") ? "mp4" : "webm";
            file = (pintura as any).blobToFile(
              dest,
              `pintura-video-${Date.now()}.${ext}`,
              effectiveMime
            );
          }
        } catch {}

        if (!file) {
          finalize(null);
          editor.close();
          return;
        }
        void (async () => {
          const meta = await getVideoMetadataFromFile(file);
          const result: PinturaVideoModalResult = meta
            ? {
                file,
                width: meta.width,
                height: meta.height,
                durationSeconds: meta.durationSeconds || undefined,
                size: `${meta.width}x${meta.height}`,
                aspectRatio: `${meta.width}x${meta.height}`,
              }
            : { file };
          finalize(result);
          editor.close();
        })();
      });

      editor.on("close", () => finalize(null));
      editor.on("loaderror", () => {
        try {
          toast.error(t("Failed to load video in editor"));
        } catch {}
        finalize(null);
      });
    });
  } catch (e) {
    console.error(e);
    try {
      toast.error(t("Failed to open video editor"));
    } catch {}
    return null;
  }
}


